/* URBAN labels use the original bag variant, retaining its stock and SKU. */
(() => {
  if (customElements.get('zk-urban-personalization')) return;
  class UrbanPersonalization extends HTMLElement {
    connectedCallback() {
      // Moving this same element keeps the customer's name and selected option.
      if (this.relocating) return;
      this.form = this.closest('form');
      if (!this.form) return;
      this.input = this.querySelector('[data-urban-name]');
      this.toggle = this.querySelector('[data-urban-enabled]');
      this.fields = this.querySelector('[data-urban-fields]');
      this.variants = JSON.parse(this.querySelector('[data-urban-variants]').textContent);
      this.controller = new AbortController();
      const signal = this.controller.signal;
      // Mobile placement is outside the form, so explicitly retain form ownership.
      const formId = this.form.getAttribute('id');
      this.input.setAttribute('form', formId);
      this.toggle.setAttribute('form', formId);
      this.toggle.addEventListener('change', () => this.sync(), {signal});
      this.form.addEventListener('change', () => this.sync(), {signal});
      this.input.addEventListener('input', () => this.refreshPreview(), {signal});
      this.form.addEventListener('submit', event => {
        if (!this.toggle.checked) return;
        this.input.value = this.input.value.normalize('NFC').trim().replace(/\s+/gu, ' ');
        this.refreshPreview();
        if (!this.input.checkValidity()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.input.reportValidity();
          this.input.focus();
          return;
        }
        event.preventDefault();
        event.stopImmediatePropagation();
        this.addPersonalizedBag();
      }, {capture: true, signal});
      this.observer = new MutationObserver(() => this.sync());
      const container = this.form.querySelector('[data-render^="product-buy-buttons-input-hidden"]');
      if (container) this.observer.observe(container, {childList: true, subtree: true});
      this.sync();
      this.mobileContainer = this.form.closest('.prd-block-info');
      if (this.mobileContainer) {
        this.home = document.createComment('URBAN name option desktop position');
        this.before(this.home);
        this.mobileViewport = window.matchMedia('(max-width: 991px)');
        this.mobileViewport.addEventListener('change', () => this.updatePlacement(), {signal});
        this.updatePlacement();
      }
    }
    disconnectedCallback() {
      if (this.relocating) return;
      this.controller?.abort();
      this.observer?.disconnect();
    }
    updatePlacement() {
      if (!this.home?.isConnected || !this.mobileContainer?.isConnected) return;
      const focusedInput = this.contains(document.activeElement) ? document.activeElement : null;
      this.relocating = true;
      try {
        if (this.mobileViewport.matches) {
          if (this.mobileContainer.firstElementChild !== this) this.mobileContainer.prepend(this);
        } else if (this.home.nextSibling !== this) {
          this.home.after(this);
        }
      } finally {
        this.relocating = false;
      }
      focusedInput?.focus({preventScroll: true});
    }
    sync() {
      this.fields.hidden = !this.toggle.checked;
      this.input.disabled = !this.toggle.checked;
      this.input.required = this.toggle.checked;
      const variant = this.variants[this.form.querySelector('[name="id"]')?.value];
      if (variant) {
        const image = this.querySelector('[data-name-product-image]');
        if (image.getAttribute('src') !== variant.src) {
          image.removeAttribute('srcset');
          image.src = variant.src;
        }
        const dy = variant.color === 'Khaki' ? 20 : ['Green', 'Grøn'].includes(variant.color) ? 7 : 0;
        this.querySelector('[data-name-position]').setAttribute('transform', `translate(0 ${dy})`);
        const total = (variant.price + Number(this.dataset.addonPrice)) / 100;
        this.querySelector('[data-urban-total]').textContent = 'Taske inkl. navnelabel: ' + new Intl.NumberFormat('da-DK', {style: 'currency', currency: 'DKK', maximumFractionDigits: 2}).format(total);
      }
      this.refreshPreview();
    }
    refreshPreview() {
      const value = this.input.value.normalize('NFC');
      let error = '';
      if (!value.trim()) error = 'Skriv det navn, der skal stå på labelet.';
      else if (value.length > 15) error = 'Der må højst være 15 tegn.';
      else if (!/^[\p{L}\p{M}\p{N} .’'\-]+$/u.test(value)) error = 'Brug bogstaver, tal, mellemrum, punktum, bindestreg eller apostrof.';
      this.input.setCustomValidity(this.toggle.checked ? error : '');
      const name = value.trim() || 'Dit navn';
      const badge = this.querySelector('[data-name-badge]');
      const text = badge.querySelector('[data-name-preview]');
      text.textContent = name;
      text.setAttribute('font-size', 15);
      const length = text.getComputedTextLength();
      const count = Array.from(name).length;
      const width = count > 10 ? Math.min(224, Math.max(124 + (count - 10) * 10, Math.ceil(length) + 15)) : 124;
      badge.querySelector('[data-name-badge-background]').setAttribute('width', width);
      badge.querySelector('[data-name-badge-stitching]').setAttribute('width', width - 6);
      text.setAttribute('x', 218 + width / 2);
      if (length > width - 15) text.setAttribute('font-size', 15 * (width - 15) / length);
      this.querySelector('[data-name-count]').textContent = `${value.length}/15`;
    }
    getItems(formData) {
      if (!this.toggle.checked) return null;
      const id = Number(formData.get('id'));
      const quantity = Math.max(1, Number(formData.get('quantity')) || 1);
      const properties = {};
      for (const [key, value] of formData.entries()) {
        const match = /^properties\[(.+)\]$/.exec(key);
        if (match) properties[match[1]] = value;
      }
      // Distinct configurations remain separate even when they use the same colour.
      properties._zk_urban_label = crypto.randomUUID();
      const bag = {id, quantity, properties};
      if (formData.get('selling_plan')) bag.selling_plan = formData.get('selling_plan');
      const color = this.variants[id]?.color || '';
      return [bag, {
        id: Number(this.dataset.addonId), quantity, parent_id: id,
        properties: {Navn: this.input.value.trim(), Trykfarve: 'Hvid', Taske: `URBAN · ${({Black: 'Sort', Green: 'Armygrøn', Khaki: 'Sand'})[color] || color}`}
      }];
    }
    async addPersonalizedBag() {
      if (this.busy) return;
      this.busy = true;
      const button = this.form.querySelector('[type="submit"]');
      const error = this.querySelector('[data-urban-error]');
      error.hidden = true;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST', headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({items: this.getItems(new FormData(this.form))})
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || 'Tasken kunne ikke lægges i kurven. Prøv igen.');
        window.location.assign(window.Shopify.routes.root + 'cart');
      } catch (failure) {
        error.textContent = failure.message;
        error.hidden = false;
      } finally {
        this.busy = false;
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('zk-urban-personalization', UrbanPersonalization);

  // Keep each paid label at the quantity of its own parent bag after cart edits.
  window.zkUrbanSyncLabels = async (cart, sections) => {
    if (!cart.items || cart.errors || cart.status) return cart;
    const updates = {};
    for (const label of cart.items) {
      if (label.handle !== 'navnelabel-til-urban' || !label.parent_relationship) continue;
      const parent = cart.items.find(item => item.key === label.parent_relationship.parent_key);
      if (parent?.properties?._zk_urban_label && label.quantity !== parent.quantity) updates[label.key] = parent.quantity;
    }
    if (!Object.keys(updates).length) return cart;
    const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST', headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({updates, sections, sections_url: window.location.pathname})
    });
    if (!response.ok) throw new Error('Navnelabelets antal kunne ikke opdateres. Genindlæs kurven.');
    return response.json();
  };

  // Lumia can load its cart class from either separate assets or a bundled asset.
  // Extend only quantity edits on marked URBAN bags; all other edits use the theme.
  customElements.whenDefined('cart-items').then(() => {
    const prototype = customElements.get('cart-items').prototype;
    const original = prototype.updateQuantity;
    prototype.updateQuantity = async function(line, quantity, name) {
      if (!document.getElementById(`CartItem-${line}`)?.hasAttribute('data-zk-urban-label-parent')) {
        return original.call(this, line, quantity, name);
      }
      const sections = [...new Set(this.getSectionsToRender().filter(item => document.getElementById(item.id)).map(item => item.section))].filter(Boolean).slice(0, 5);
      this.enableLoading(line);
      try {
        const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
          method: 'POST', headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({line: Number(line), quantity: Number(quantity), sections, sections_url: window.location.pathname})
        });
        let cart = await response.json();
        if (!response.ok || cart.errors) throw new Error(cart.description || cart.errors || 'Antallet kunne ikke ændres.');
        cart = await window.zkUrbanSyncLabels(cart, sections);
        this.renderContentsAndDependencies(cart, line);
      } catch (failure) {
        const error = document.getElementById(`Line-item-error-${line}`);
        if (error) {
          error.hidden = false;
          error.classList.remove('hidden');
          error.querySelector('.cart-item__error-text').textContent = failure.message;
        }
      } finally {
        this.querySelectorAll('.loading-overlay').forEach(item => item.classList.add('hidden'));
        this.disableLoading();
      }
    };
  });
})();
